import React, { useState } from 'react';
import { Phone, Mail, Calendar, Clock, MessageCircle, X, User } from 'lucide-react';
import { formatISTDateTimeShort, formatISTDateShort } from '../../utils/date';

const NewStatusBadge = ({ status }) => {
  const getStatusStyles = () => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'done':
        return 'bg-green-100 text-green-700 border-2 border-green-200';
      case 'in-progress':
      case 'inprogress':
      case 'in_progress':
        return 'bg-[#2D869C] text-white border-2 border-[#2D869C]';
      case 'cancelled':
      case 'canceled':
        return 'bg-red-100 text-red-700 border-2 border-red-200';
      case 'scheduled':
      case 'confirmed':
        return 'bg-blue-100 text-blue-700 border-2 border-blue-200';
      case 'checked-in':
      case 'checkedin':
        return 'bg-[#2D869C] text-white border-2 border-[#2D869C]';
      default:
        return 'bg-gray-100 text-gray-700 border-2 border-gray-200';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_|-/g, ' ');
  };

  return (
    <span className={`px-3 py-1.5 text-sm font-semibold rounded-lg ${getStatusStyles()}`}>
      {formatStatus(status)}
    </span>
  );
};

// Complaint Popup Component
const ComplaintPopup = ({ complaint, onClose }) => {
  if (!complaint) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border-2 border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <MessageCircle size={20} />
            Chief Complaint
          </h3>
          <button 
            onClick={onClose}
            className="text-white/90 hover:text-white transition-colors p-1 hover:bg-white/20 rounded-lg"
          >
            <X size={22} />
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700 text-md font-medium leading-relaxed whitespace-pre-wrap">
            {complaint}
          </p>
        </div>
      </div>
    </div>
  );
};

const NewAppointmentCard = ({ appointment }) => {
  const [showComplaint, setShowComplaint] = useState(false);
  
  const patientName = appointment.patient?.firstName
    ? `${appointment.patient.firstName} ${appointment.patient.lastName || ''}`.trim()
    : 'Unknown Patient';

  const doctorName = appointment?.doctor?.name || 'Unknown Doctor';

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-5 hover:shadow-lg hover:border-[#2D869C] transition-all duration-200">
      {/* Top Row - Doctor Name and Status */}
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-md font-semibold text-gray-800">
          Dr. {doctorName}
        </h3>
        <NewStatusBadge status={appointment.status || 'scheduled'} />
      </div>

      {/* Content Row - Patient Info and Image */}
      <div className="flex gap-4">
        {/* Left - Patient Details */}
        <div className="flex-1">
          {/* Patient Name */}
          <p className="text-[#2D869C] font-semibold text-md mb-3">
            Patient Name: {patientName}
          </p>

          {/* Contact Info */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center text-sm font-medium text-gray-600">
              <Phone size={14} className="mr-2 text-gray-400" />
              <span>{appointment.patient?.mobile || appointment.patient?.phone || 'N/A'}</span>
            </div>
            {appointment.patient?.email && (
              <div className="flex items-center text-sm font-medium text-gray-600">
                <Mail size={14} className="mr-2 text-gray-400" />
                <span className="truncate">{appointment.patient.email}</span>
              </div>
            )}
          </div>

          {/* Badges Row */}
          <div className="flex flex-wrap gap-2">
            {/* Date Badge */}
            <span className="inline-flex items-center px-3 py-2 bg-white border-2 border-[#2D869C] text-[#2D869C] text-sm font-semibold rounded-lg">
              <Calendar size={14} className="mr-2" />
              {formatISTDateShort(appointment.dateTime)}
            </span>

            {/* Time Badge */}
            <span className="inline-flex items-center px-3 py-2 bg-white border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg">
              <Clock size={14} className="mr-2" />
              {formatISTDateTimeShort(appointment.dateTime)}
            </span>

            {/* Complaint Badge - Clickable */}
            {appointment.complaint && (
              <button 
                onClick={() => setShowComplaint(true)}
                className="inline-flex items-center px-3 py-2 bg-amber-50 border-2 border-amber-300 text-amber-700 text-sm font-semibold rounded-lg hover:bg-amber-100 transition-colors cursor-pointer shadow-sm hover:shadow-md"
              >
                <MessageCircle size={14} className="mr-2" />
                View Complaint
              </button>
            )}
          </div>
        </div>

        {/* Right - Patient Image */}
        <div className="flex-shrink-0">
          <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center overflow-hidden">
            {appointment.patient?.photo?.url ? (
              <img
                src={appointment.patient.photo.url}
                alt={patientName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <User size={36} className="text-gray-400" />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Complaint Popup */}
      {showComplaint && (
        <ComplaintPopup 
          complaint={appointment.complaint} 
          onClose={() => setShowComplaint(false)} 
        />
      )}
    </div>
  );
};

export default NewAppointmentCard;
